import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../lib/api';
import { xcg, humanWaiting, humanArrival, hoursSince, cwPrettyDate } from '../lib/format';
import { Loader2 } from 'lucide-react';

function nextAction(b) {
  const h = hoursSince(b.CreatedAt);
  if (h < 24) return { text: 'First reminder goes out at 24 hours', urgency: 0 };
  if (h < 72) return { text: 'Reminded once. Deadline notice at 72 hours', urgency: 1 };
  return { text: 'Final notice sent. Hold releases automatically', urgency: 2 };
}

function daysUntil(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const target = Date.UTC(y, m - 1, d);
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((target - today) / 86400000);
}

export default function Holds() {
  const [bookings, setBookings] = useState(null);

  useEffect(() => { api.get('/bookings').then(r => setBookings(r.data)); }, []);

  const holds = useMemo(() => {
    if (!bookings) return [];
    return bookings
      .filter(b => b.Status === 'Held')
      .sort((a, b) => hoursSince(b.CreatedAt) - hoursSince(a.CreatedAt));
  }, [bookings]);

  if (!bookings) return <div className="flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</div>;

  return (
    <div>
      <div className="mb-5">
        <h1 className="font-heading text-3xl sm:text-4xl text-[#1F5A24]">Holds at risk</h1>
        <p className="text-[#333]/70 mt-1">Unverified holds and where they are in the reminder cycle.</p>
      </div>

      {holds.length === 0 ? (
        <div className="text-[#333]/60 bg-[#FBFCFB] border border-[#CFD8CF] rounded-xl p-8 text-center">
          No holds at risk. Every booking has been reviewed.
        </div>
      ) : (
        <ul className="space-y-3">
          {holds.map((b) => {
            const na = nextAction(b);
            const arrivingSoon = daysUntil(b.CheckIn) <= 2 && daysUntil(b.CheckIn) >= 0;
            const bg = arrivingSoon ? '#FFF3E0' : '#FBFCFB';
            return (
              <li
                key={b['Booking#']}
                data-testid={`hold-${b['Booking#']}`}
                className="rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                style={{ backgroundColor: bg, borderColor: arrivingSoon ? '#B26A00' : '#CFD8CF' }}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-medium">{b.Name}</div>
                    <span className="text-xs text-[#333]/60">· {b['Booking#']}</span>
                  </div>
                  <div className="text-sm text-[#333]/80 mt-0.5">
                    {xcg(b.Amount)} · {b.PaymentMethod} · check-in {cwPrettyDate(b.CheckIn)} ({humanArrival(b.CheckIn)})
                  </div>
                  <div className="text-sm text-[#B26A00] mt-1">
                    {arrivingSoon ? 'Arriving soon, still unverified. ' : ''}{na.text}.
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs uppercase tracking-widest text-[#333]/60">Waiting</div>
                  <div className="font-heading text-lg text-[#1F5A24]">{humanWaiting(b.CreatedAt).replace('waiting ', '')}</div>
                  <div className="text-xs text-[#333]/60">Reminders {b.ReminderCount ?? 0} / 3</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
